import { holdsAnywhere } from '@carii/casl-abilities';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { Textarea } from '../../components/ui/FormField.js';
import { Modal } from '../../components/ui/Modal.js';
import { useAbility } from '../../context/AbilityContext.js';
import { type DiscountCode, discountsApi } from '../../services/discounts.js';

const REASON_MAX = 300;

/** 8 Oct 2026, 15:10 — Malaysia time. */
export const mytTime = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleString('en-MY', {
        timeZone: 'Asia/Kuala_Lumpur',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
    : '';

/** 391: who the code was shared with — the account (linked, with email), or a pre-391 note. */
export function SharedWithCell({ code }: { code: DiscountCode }) {
  if (!code.shared) return <span className="text-steel">Not shared</span>;
  const u = code.shared_with_user;
  if (!u) return <span>{code.shared_with ?? '—'}</span>;
  return (
    <div className="flex flex-col">
      <Link to={`/users/${u.id}`} className="text-primary hover:underline">
        {u.full_name || code.shared_with || u.id}
      </Link>
      {u.email && <span className="text-xs text-steel">{u.email}</span>}
    </div>
  );
}

export function ShareBookingCell({ code }: { code: DiscountCode }) {
  if (!code.share_booking_id) return <span className="text-steel">—</span>;
  return (
    <Link
      to={`/bookings/${code.share_booking_id}`}
      className="font-mono text-xs text-primary hover:underline"
    >
      {code.share_booking_id.slice(-8)}
    </Link>
  );
}

export function GivenByCell({ code }: { code: DiscountCode }) {
  if (!code.shared) return <span className="text-steel">—</span>;
  if (!code.shared_with_user)
    return <span className="text-xs text-steel">Shared before approvals</span>;
  return (
    <div className="flex flex-col">
      {code.shared_by ? (
        <Link to={`/users/${code.shared_by.id}`} className="text-primary hover:underline">
          {code.shared_by.label || 'Staff'}
        </Link>
      ) : (
        <span>—</span>
      )}
      <span className="text-xs text-steel">{mytTime(code.shared_at)}</span>
    </div>
  );
}

/**
 * 391 (FR-004, mock-up screens 1 and 3): the share's approval, its decider and reason, and — when the platform says this
 * caller may decide — Approve and Reject… (a reason required). The sharer's own share says someone else must decide.
 */
export function ShareApprovalCell({
  code,
  discountId,
}: { code: DiscountCode; discountId: string }) {
  const ability = useAbility();
  const queryClient = useQueryClient();
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['discount-codes', discountId] });

  const approve = useMutation({
    mutationFn: () => discountsApi.approveShare(discountId, code.id, code.shared_at as string),
    onSuccess: refresh,
    onError: refresh,
  });
  const reject = useMutation({
    mutationFn: () =>
      discountsApi.rejectShare(discountId, code.id, code.shared_at as string, reason.trim()),
    onSuccess: () => {
      setRejecting(false);
      setReason('');
      refresh();
    },
  });

  const a = code.share_approval;
  if (!code.shared) return <span className="text-steel">—</span>;
  if (!a) return <span className="text-xs text-steel">No approval needed</span>;

  const mayDecide =
    code.share_decision_for === 'allowed' && holdsAnywhere(ability as never, 'approve', 'Discount');

  return (
    <div className="flex min-w-[12rem] flex-col gap-1">
      <span>
        {a.status === 'pending' && <Badge variant="warning">Pending approval</Badge>}
        {a.status === 'approved' && <Badge variant="success">Approved</Badge>}
        {a.status === 'rejected' && <Badge variant="danger">Rejected</Badge>}
      </span>
      {a.status !== 'pending' && (
        <span data-testid="share-decision" className="text-xs text-steel">
          {`by ${a.decided_by?.label || 'someone'}${a.decided_at ? ` · ${mytTime(a.decided_at)}` : ''}`}
          {a.reason ? `: ${a.reason}` : ''}
        </span>
      )}
      {a.status === 'pending' && code.share_decision_for === 'own' && (
        <span className="text-xs text-steel">You shared this, so someone else must decide.</span>
      )}
      {a.status === 'pending' && mayDecide && (
        <div className="flex gap-2">
          <Button size="sm" loading={approve.isPending} onClick={() => approve.mutate()}>
            Approve
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setRejecting(true)}>
            Reject…
          </Button>
        </div>
      )}
      {approve.isError && (
        <span role="alert" className="text-xs text-red-600">
          {(approve.error as { message?: string }).message ?? 'Could not approve.'}
        </span>
      )}
      <Modal
        open={rejecting}
        onClose={() => setRejecting(false)}
        title={`Reject share of ${code.code}`}
      >
        <div className="space-y-4 px-6 py-4 text-sm text-ink">
          <p className="text-steel">
            The code stays unusable. The reason is kept on the code and shown to staff.
          </p>
          <div className="flex flex-col gap-1">
            <label htmlFor={`reject-reason-${code.id}`} className="font-medium">
              Reason
            </label>
            <Textarea
              id={`reject-reason-${code.id}`}
              rows={3}
              maxLength={REASON_MAX}
              value={reason}
              onInput={(e) => setReason((e.target as HTMLTextAreaElement).value)}
            />
            <p className="text-right text-xs text-steel">
              {reason.length} / {REASON_MAX}
            </p>
          </div>
          {reject.isError && (
            <p role="alert" className="text-red-600">
              {(reject.error as { message?: string }).message ?? 'Could not reject.'}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="secondary" onClick={() => setRejecting(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              variant="danger"
              disabled={reason.trim().length === 0}
              loading={reject.isPending}
              onClick={() => reject.mutate()}
            >
              Reject share
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
